"use client";

import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib";
import type { MediaPlayerTheme } from "../media-player/media-player-theme";
import { TOPIC_CARD_PLAYER_THEME } from "../media-player/media-player-theme";
import { MediaPlayerTrackGlow } from "../media-player/media-player-track-glow";
import { MediaPlayerTrackParticles } from "../media-player/media-player-track-particles";
import { buildMediaPlayerWaveformHeights } from "../media-player/media-player-waveform-bars";
import { useMediaPlayerScrub } from "../media-player/use-media-player-scrub";
import {
	type AudioWaveformBarAlignment,
	resolveRevealedBarRect,
	resolveWaveformBarRect,
} from "./audio-waveform-bar-geometry";
import {
	type UseMediaElementWaveformOptions,
	useMediaElementWaveform,
} from "./use-media-element-waveform";
import { useAudioElementProgressPercent } from "./use-smooth-media-values";
import {
	DEFAULT_BAR_STRETCH_BOUNCE_MS,
	DEFAULT_BUFFER_REVEAL_WIPE_MS,
	resolveBarStartPercent,
	resolveBarStretchWavefrontMs,
	resolvePerBarLinearStretchProgress,
	resolvePerBarStretchProgress,
} from "./waveform-bar-visual";
import type { WaveformLevelTimeline } from "./waveform-level-timeline";

export type { AudioWaveformBarAlignment } from "./audio-waveform-bar-geometry";

/** Desert biome ambient colors aligned with learn topic presentation. */
export const DESERT_AUDIO_WAVEFORM_AMBIENT = {
	trackGlowColor1: "#ffc100",
	trackGlowColor2: "#ff002c",
	ambientParticleColor: "#ffe08a",
} as const;

export interface AudioWaveformAmbientTheme {
	trackGlowColor1: string;
	trackGlowColor2: string;
	ambientParticleColor: string;
}

export interface AudioWaveformVisualizerProps
	extends Pick<UseMediaElementWaveformOptions, "waveformSeed" | "barCount"> {
	/** Audible element — progress/scrub transport. */
	audioRef: RefObject<HTMLAudioElement | null>;
	/** Precomputed levels keyed to transport currentTime — deterministic sync. */
	waveformTimeline?: WaveformLevelTimeline | null;
	isActive: boolean;
	isPlaying?: boolean;
	/** True while buffering/decoding before first audible play. */
	isPreparingPlayback?: boolean;
	/** True while transport is loading (spinner) — hides playhead track and aurora glow only. */
	isLoading?: boolean;
	/** Authoritative playhead from the playback controller. */
	progressPercent: number;
	/** Smoothed 0–100 buffer reveal during prepare (clip-path + bar stretch). */
	displayRevealPercent?: number;
	/** Resets per-bar stretch timers when a new buffer session starts. */
	bufferRevealSessionKey?: number;
	/** Monotonic ms when the current buffer reveal session started. */
	bufferRevealSessionStartMs?: number;
	/** Left-to-right wipe duration for per-bar stretch scheduling. */
	bufferRevealWipeDurationMs?: number;
	ambientTheme?: AudioWaveformAmbientTheme;
	showAmbient?: boolean;
	barAlignment?: AudioWaveformBarAlignment;
	scrubbable?: boolean;
	/** Minimum scrub hit target height in px (default: full track height). */
	scrubHitHeightPx?: number;
	volumePercent?: number;
	onScrubPause?: () => void;
	onSeekCommit?: (percent: number) => void;
	seekEpoch?: number;
	/** Ambient glow spans the full track width (not playhead-masked). */
	ambientGlowFullWidth?: boolean;
	/** Hide dim placeholder dots ahead of the buffer-reveal wavefront. */
	hideUnrevealedPrepareDots?: boolean;
	/** Opacity multiplier for buffer-reveal stretch bars (default: dim prepare). */
	prepareStretchBarOpacity?: number;
	/** Animate bars without audio analysis (mock / placeholder playback). */
	mockDancingBars?: boolean;
	/** Read live levels from the audible transport element (live streams). */
	useSharedTransportAnalyser?: boolean;
	className?: string;
}

const WAVE_HEIGHT_PX = 22;
const BAR_OPACITY_TRANSITION_MS = 280;
const PREPARE_DIM_OPACITY = 0.22;
/** Vertical headroom so bounce overshoot is not clipped by the buffer wipe mask. */
const BUFFER_BOUNCE_CLIP_PAD_Y = WAVE_HEIGHT_PX * 0.65;

export function AudioWaveformVisualizer({
	audioRef,
	waveformTimeline = null,
	isActive,
	isPlaying = false,
	isPreparingPlayback = false,
	isLoading = false,
	progressPercent,
	displayRevealPercent = 0,
	bufferRevealSessionKey = 0,
	bufferRevealSessionStartMs = 0,
	bufferRevealWipeDurationMs = DEFAULT_BUFFER_REVEAL_WIPE_MS,
	waveformSeed,
	barCount,
	ambientTheme = DESERT_AUDIO_WAVEFORM_AMBIENT,
	showAmbient = true,
	barAlignment = "center",
	scrubbable = true,
	scrubHitHeightPx,
	volumePercent = 100,
	onScrubPause,
	onSeekCommit,
	seekEpoch = 0,
	ambientGlowFullWidth = false,
	hideUnrevealedPrepareDots = false,
	prepareStretchBarOpacity = PREPARE_DIM_OPACITY,
	mockDancingBars = false,
	useSharedTransportAnalyser = false,
	className,
}: AudioWaveformVisualizerProps) {
	const trackRef = useRef<HTMLDivElement>(null);
	const bufferClipPathId = useId().replace(/:/g, "");
	const playheadClipPathId = useId().replace(/:/g, "");
	const bufferRevealSessionStartMsRef = useRef(bufferRevealSessionStartMs);
	const lastBufferSessionKeyRef = useRef(bufferRevealSessionKey);
	const [reduceMotion, setReduceMotion] = useState(false);
	const [stretchNowMs, setStretchNowMs] = useState(0);
	const volumeScale = Math.max(0, Math.min(1, volumePercent / 100));
	const liveProgressPercent = useAudioElementProgressPercent(
		audioRef,
		isActive,
		progressPercent,
	);
	const { barHeights } = useMediaElementWaveform({
		audioRef,
		transportAudioRef: audioRef,
		waveformTimeline,
		enabled:
			isActive &&
			(isPlaying || mockDancingBars) &&
			!isPreparingPlayback &&
			!isLoading,
		mockDancingBars,
		allowSharedTransportAnalyser: useSharedTransportAnalyser,
		waveformSeed,
		barCount,
	});
	const staticBarHeights = useMemo(
		() =>
			buildMediaPlayerWaveformHeights(
				waveformSeed ?? "audio-waveform",
				barCount ?? barHeights.length,
			),
		[waveformSeed, barCount, barHeights.length],
	);

	const handleScrubPause = useCallback(() => {
		onScrubPause?.();
	}, [onScrubPause]);

	const handleSeekCommit = useCallback(
		(percent: number) => {
			onSeekCommit?.(percent);
		},
		[onSeekCommit],
	);

	const {
		playheadPercent,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	} = useMediaPlayerScrub({
		progressPercent: liveProgressPercent,
		maxSeekPercent: 100,
		interactive:
			scrubbable &&
			!isPreparingPlayback &&
			!isLoading &&
			onSeekCommit != null,
		seekEpoch,
		trackBoundsRef: trackRef,
		onPause: handleScrubPause,
		onSeekCommit: handleSeekCommit,
	});

	const stopPropagation = useCallback(
		(handler: (event: ReactPointerEvent<HTMLElement>) => void) =>
			(event: ReactPointerEvent<HTMLElement>) => {
				event.stopPropagation();
				handler(event);
			},
		[],
	);

	const particleTheme = useMemo<MediaPlayerTheme>(
		() => ({
			...TOPIC_CARD_PLAYER_THEME,
			ambientParticleColor: ambientTheme.ambientParticleColor,
			trackGlowColor1: ambientTheme.trackGlowColor1,
			trackGlowColor2: ambientTheme.trackGlowColor2,
		}),
		[ambientTheme],
	);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduceMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	useEffect(() => {
		if (bufferRevealSessionKey !== lastBufferSessionKeyRef.current) {
			bufferRevealSessionStartMsRef.current =
				bufferRevealSessionStartMs > 0
					? bufferRevealSessionStartMs
					: performance.now();
			lastBufferSessionKeyRef.current = bufferRevealSessionKey;
		}
	}, [bufferRevealSessionKey, bufferRevealSessionStartMs]);

	useEffect(() => {
		if (bufferRevealSessionStartMs > 0) {
			bufferRevealSessionStartMsRef.current = bufferRevealSessionStartMs;
		}
	}, [bufferRevealSessionStartMs]);

	const isBufferRevealingForEffect =
		isActive && (isPreparingPlayback || isLoading);

	useEffect(() => {
		if (!isBufferRevealingForEffect) {
			return;
		}

		let frame = 0;
		const tick = () => {
			setStretchNowMs(performance.now());
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [isBufferRevealingForEffect, bufferRevealSessionKey]);

	if (!isActive) {
		return null;
	}

	const hideProgressTrack = isPreparingPlayback || isLoading;
	const isBufferRevealing = hideProgressTrack;
	const showAmbientParticles = showAmbient && !reduceMotion;
	const showTrackGlow =
		showAmbientParticles && (!hideProgressTrack || ambientGlowFullWidth);
	const ambientGlowPlayheadPercent = ambientGlowFullWidth
		? 100
		: playheadPercent;
	const barHeightsForRender = isBufferRevealing
		? staticBarHeights
		: barHeights;
	const barCountForRender = barHeightsForRender.length;
	const barTransitionStyle =
		reduceMotion || isBufferRevealing
			? undefined
			: {
					transition: `opacity ${BAR_OPACITY_TRANSITION_MS}ms ease-out`,
				};
	const viewWidth = barCountForRender * 10;
	const bufferClipWidth = (displayRevealPercent / 100) * viewWidth;
	const playheadClipWidth = (playheadPercent / 100) * viewWidth;
	const barOptions = {
		waveHeightPx: WAVE_HEIGHT_PX,
		alignment: barAlignment,
	};

	const renderPrepareDotBars = () => {
		if (hideUnrevealedPrepareDots) {
			return null;
		}

		return barHeightsForRender.map((height, index) => {
			const barStart = resolveBarStartPercent(index, barCountForRender);
			if (displayRevealPercent >= barStart) {
				return null;
			}

			const normalizedHeight = height * volumeScale;
			const rect = resolveRevealedBarRect(
				normalizedHeight,
				index,
				0,
				barOptions,
			);

			return (
				<rect
					key={`prepare-dot-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={Math.min(rect.width, rect.height) / 2}
					ry={Math.min(rect.width, rect.height) / 2}
					className="fill-white dark:fill-white"
					style={{ opacity: PREPARE_DIM_OPACITY }}
				/>
			);
		});
	};

	const renderPrepareStretchBars = () => {
		const nowMs = stretchNowMs || performance.now();
		const sessionStartMs =
			bufferRevealSessionStartMsRef.current > 0
				? bufferRevealSessionStartMsRef.current
				: nowMs;

		return barHeightsForRender.map((height, index) => {
			const barStart = resolveBarStartPercent(index, barCountForRender);
			if (displayRevealPercent < barStart) {
				return null;
			}

			const wavefrontMs = resolveBarStretchWavefrontMs(
				index,
				barCountForRender,
				bufferRevealWipeDurationMs,
			);
			const elapsedMs = Math.max(0, nowMs - sessionStartMs - wavefrontMs);
			const linearStretch = resolvePerBarLinearStretchProgress(elapsedMs);
			const stretchProgress = resolvePerBarStretchProgress(
				elapsedMs,
				DEFAULT_BAR_STRETCH_BOUNCE_MS,
				!reduceMotion,
			);
			const normalizedHeight = height * volumeScale;
			const rect = resolveRevealedBarRect(
				normalizedHeight,
				index,
				stretchProgress,
				barOptions,
			);
			const cornerRadius =
				linearStretch < 1 ? Math.min(rect.width, rect.height) / 2 : 2;

			return (
				<rect
					key={`prepare-stretch-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={cornerRadius}
					ry={cornerRadius}
					className="fill-white dark:fill-white"
					style={{
						opacity: prepareStretchBarOpacity * linearStretch,
					}}
				/>
			);
		});
	};

	const renderPlaybackBars = (opacityScale: number) =>
		barHeightsForRender.map((height, index) => {
			const normalizedHeight = height * volumeScale;
			const rect = resolveWaveformBarRect(
				normalizedHeight,
				index,
				barOptions,
			);

			return (
				<rect
					key={`wave-bar-${opacityScale}-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={2}
					ry={2}
					className="fill-white dark:fill-white"
					style={{
						opacity: opacityScale,
						...barTransitionStyle,
					}}
				/>
			);
		});

	return (
		<div
			className={cn(
				"relative isolate h-8 min-w-0 flex-1 overflow-visible",
				className,
			)}
		>
			{showAmbientParticles ? (
				<>
					<MediaPlayerTrackParticles theme={particleTheme} />
					{showTrackGlow ? (
						<MediaPlayerTrackGlow
							playheadPercent={ambientGlowPlayheadPercent}
							className="-top-3 -bottom-3"
							color1={ambientTheme.trackGlowColor1}
							color2={ambientTheme.trackGlowColor2}
							brightness={1.05}
							bandSpread={1.3}
							bandHeight={0.5}
						/>
					) : null}
				</>
			) : null}
			{/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: false positive - the role is set conditionally on the same element and the ARIA attributes are only present when it is */}
			<div
				ref={trackRef}
				className="relative z-[1] flex h-full w-full touch-none select-none items-center overflow-visible px-0.5"
				role={scrubbable && !hideProgressTrack ? "slider" : undefined}
				aria-valuemin={scrubbable && !hideProgressTrack ? 0 : undefined}
				aria-valuemax={
					scrubbable && !hideProgressTrack ? 100 : undefined
				}
				aria-valuenow={
					scrubbable && !hideProgressTrack
						? Math.round(playheadPercent)
						: undefined
				}
				aria-hidden={
					scrubbable && !hideProgressTrack ? undefined : true
				}
			>
				<svg
					className="pointer-events-none h-[22px] w-full overflow-visible"
					viewBox={`0 0 ${viewWidth} ${WAVE_HEIGHT_PX}`}
					overflow="visible"
					preserveAspectRatio="none"
					aria-hidden="true"
				>
					{isBufferRevealing ? (
						<>
							<defs>
								<clipPath id={bufferClipPathId}>
									<rect
										x={0}
										y={-BUFFER_BOUNCE_CLIP_PAD_Y}
										width={bufferClipWidth}
										height={
											WAVE_HEIGHT_PX +
											BUFFER_BOUNCE_CLIP_PAD_Y * 2
										}
									/>
								</clipPath>
							</defs>
							<g>{renderPrepareDotBars()}</g>
							<g clipPath={`url(#${bufferClipPathId})`}>
								{renderPrepareStretchBars()}
							</g>
						</>
					) : !hideProgressTrack ? (
						<>
							<defs>
								<clipPath id={playheadClipPathId}>
									<rect
										x={0}
										y={0}
										width={playheadClipWidth}
										height={WAVE_HEIGHT_PX}
									/>
								</clipPath>
							</defs>
							<g>{renderPlaybackBars(0.22)}</g>
							<g clipPath={`url(#${playheadClipPathId})`}>
								{renderPlaybackBars(1)}
							</g>
						</>
					) : null}
				</svg>
				{scrubbable && !hideProgressTrack && onSeekCommit ? (
					<div
						className={cn(
							"absolute left-0 z-[5] w-full min-w-[12px] cursor-pointer",
							scrubHitHeightPx != null
								? "top-1/2 -translate-y-1/2"
								: "inset-y-0",
						)}
						style={
							scrubHitHeightPx != null
								? {
										height: scrubHitHeightPx,
										minHeight: scrubHitHeightPx,
									}
								: undefined
						}
						onPointerDown={stopPropagation(onPointerDown)}
						onPointerMove={stopPropagation(onPointerMove)}
						onPointerUp={stopPropagation(onPointerUp)}
						onPointerLeave={stopPropagation(onPointerLeave)}
						onPointerCancel={stopPropagation(onPointerCancel)}
						onClick={(event) => event.stopPropagation()}
					/>
				) : null}
			</div>
		</div>
	);
}
